import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { FaBars, FaTimes, FaChevronDown } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

/* =======================
   PAGE COMPONENT
======================= */

const TermsConditions = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  // 🔓 Expand ALL sections by default
  const [openIndex, setOpenIndex] = useState("all");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const toggle = (i) => {
    if (openIndex === "all") {
      setOpenIndex(i);
    } else {
      setOpenIndex(openIndex === i ? null : i);
    }
  };

  return (
    <>
      {/* -------- TOP NAV -------- */}
      <TopNav>
        <NavInner>
          <Brand to="/">RechargeJunction</Brand>

          <NavLinks>
            <Link to="/">Home</Link>
            <Link to="/login">Login</Link>
            <Link to="/register">Register</Link>
            <Link to="/contact">Contact</Link>
          </NavLinks>

          <Burger onClick={() => setMenuOpen(true)}>
            <FaBars />
          </Burger>
        </NavInner>
      </TopNav>

      {/* -------- MOBILE MENU -------- */}
      <AnimatePresence>
        {menuOpen && (
          <MobileMenu
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3 }}
          >
            <Close onClick={() => setMenuOpen(false)}>
              <FaTimes />
            </Close>

            <MobileLinks>
              <Link to="/" onClick={() => setMenuOpen(false)}>Home</Link>
              <Link to="/login" onClick={() => setMenuOpen(false)}>Login</Link>
              <Link to="/register" onClick={() => setMenuOpen(false)}>Register</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)}>Contact</Link>
            </MobileLinks>
          </MobileMenu>
        )}
      </AnimatePresence>

      {/* -------- PAGE CONTENT -------- */}
      <Page>
        <Container>
          <Title>Terms & Conditions</Title>
          <Subtitle>Clear, transparent & legally binding usage terms</Subtitle>

          <Timeline>
            {SECTIONS.map((item, i) => (
              <TimelineItem key={i}>
                <Dot />
                <TimelineTitle>{item.title}</TimelineTitle>

                <Accordion>
                  <AccordionHeader onClick={() => toggle(i)}>
                    {item.subtitle}
                    <Icon
                      animate={{
                        rotate:
                          openIndex === "all" || openIndex === i ? 180 : 0,
                      }}
                    >
                      <FaChevronDown />
                    </Icon>
                  </AccordionHeader>

                  <AnimatePresence>
                    {(openIndex === "all" || openIndex === i) && (
                      <AccordionContent
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                      >
                        {item.content}
                      </AccordionContent>
                    )}
                  </AnimatePresence>
                </Accordion>
              </TimelineItem>
            ))}
          </Timeline>
        </Container>
      </Page>
    </>
  );
};

export default TermsConditions;

/* =======================
   CONTENT DATA
======================= */

const SECTIONS = [
  {
    title: "Introduction",
    subtitle: "Agreement & acceptance",
    content: (
      <>
        <p>
          The terms "We", "Us", "Our", "Company" refer to <b>Wekala</b>.
          The terms "Visitor" / "User" refer to any person accessing this website.
        </p>
        <p>
          By accessing this website, you agree to be bound by these Terms and
          Conditions. If you do not agree, please exit this site.
        </p>
      </>
    ),
  },
  {
    title: "Use of Content",
    subtitle: "Intellectual property rights",
    content: (
      <>
        <p>
          All logos, brands, marks, designs, names and content on this site are
          owned or licensed by the Company.
        </p>
        <p>
          You may not copy, reproduce, sell, distribute or modify any content
          without written permission.
        </p>
      </>
    ),
  },
  {
    title: "Security Rules",
    subtitle: "Platform protection & misuse",
    content: (
      <ul>
        <li>Unauthorized access to systems or data is prohibited</li>
        <li>No hacking, scanning, probing or security testing</li>
        <li>No viruses, malware or service disruption</li>
        <li>No spam or promotional abuse</li>
      </ul>
    ),
  },
  {
    title: "General Rules",
    subtitle: "User conduct & responsibility",
    content: (
      <ul>
        <li>No illegal or unlawful activity</li>
        <li>No abusive, defamatory or obscene content</li>
        <li>No copyright or trademark infringement</li>
      </ul>
    ),
  },
  {
    title: "Indemnification",
    subtitle: "Legal responsibility of users",
    content: (
      <p>
        Users agree to indemnify and hold harmless the Company, its employees and
        partners from any claims arising from misuse of this website.
      </p>
    ),
  },
  {
    title: "Limitation of Liability",
    subtitle: "Extent of company liability",
    content: (
      <>
        <p>
          The Company shall not be liable for any direct, indirect, incidental or
          consequential damages.
        </p>
        <p>
          Total liability shall not exceed the amount paid by the user, if any.
        </p>
      </>
    ),
  },
  {
    title: "Disclaimer",
    subtitle: "Service availability & risk",
    content: (
      <p>
        The Company shall not be responsible for loss of data, profits, business
        interruption or hardware damage arising from use of this website.
      </p>
    ),
  },
];

/* =======================
   STYLES
======================= */

const TopNav = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(8, 10, 16, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const NavInner = styled.div`
  max-width: 1100px;
  margin: auto;
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Brand = styled(Link)`
  font-size: 18px;
  font-weight: 700;
  color: #ffb97b;
  text-decoration: none;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 22px;

  a {
    color: #ddd;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      color: #ffb97b;
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Burger = styled.div`
  display: none;
  font-size: 22px;
  color: #fff;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: #080a10;
  z-index: 200;
  padding: 90px 30px;
`;

const Close = styled.div`
  position: absolute;
  top: 22px;
  right: 22px;
  font-size: 24px;
  color: #fff;
  cursor: pointer;
`;

const MobileLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 26px;

  a {
    font-size: 20px;
    font-weight: 600;
    color: #fff;
    text-decoration: none;

    &:hover {
      color: #ffb97b;
    }
  }
`;

const Page = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at top, #141823, #080a10);
  padding: 140px 20px 90px;
  color: #ddd;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: auto;
`;

const Title = styled.h1`
  font-size: 42px;
  text-align: center;
  margin-bottom: 12px;
  color: #ffffff;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  opacity: 0.85;
  margin-bottom: 70px;
  color: #d6d6d6;
`;

const Timeline = styled.div`
  position: relative;
  margin-left: 25px;
  border-left: 2px solid rgba(255, 255, 255, 0.15);
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: 50px;
  padding-left: 40px;
`;

const Dot = styled.div`
  position: absolute;
  left: -11px;
  top: 6px;
  width: 18px;
  height: 18px;
  background: #ffb97b;
  border-radius: 50%;
  box-shadow: 0 0 20px rgba(255, 185, 123, 0.6);
`;

const TimelineTitle = styled.h3`
  font-size: 22px;
  margin-bottom: 12px;
  color: #ffcc9c;
`;

const Accordion = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  overflow: hidden;
`;

const AccordionHeader = styled.div`
  padding: 18px 22px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.03);
  color: #ffffff;
  cursor: pointer;
`;

const AccordionContent = styled(motion.div)`
  padding: 0 22px 22px;
  font-size: 15px;
  line-height: 1.7;
  opacity: 0.95;
  color: #ddd;

  ul {
    padding-left: 20px;
  }

  li {
    margin-bottom: 8px;
  }
`;

const Icon = styled(motion.span)`
  display: flex;
  align-items: center;
`;